import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from '../../common/FeatherIcon';
import usePillarNav from '../Sidebar/pillarNav';
import { OWN_TAB_STRIP, TAB_CARRY, TAB_GROUPS, tabFor, tabShown } from '../Sidebar/pillarTabs';
import { readNormalizedUserFromStorage } from '../../../utils/entitlements';
import { isFeatureEnabled } from '../../../utils/featureFlags';
import { canViewBilling, isPrivilegedUser } from '../../../utils/permissions';
import './PillarTabBar.css';

/**
 * The tab strip over a page that belongs to a tab group ("New navigation",
 * pillarTabs.js): the page's related pages as tabs, drawn like the FinOps view
 * tabs. Each tab is a link to its own route, so every tab keeps its URL.
 *
 * Nothing is drawn with the switch off, outside a group, on a page that draws the
 * group's tabs itself (OWN_TAB_STRIP), or when only one of the group's tabs passes its gate.
 */
export default function PillarTabBar() {
  const { on } = usePillarNav();
  const { pathname, search } = useLocation();
  const { t } = useTranslation(['nav']);
  if (!on) return null;
  const here = tabFor(pathname, search);
  if (!here || OWN_TAB_STRIP.has(here.group)) return null;

  const user = readNormalizedUserFromStorage();
  const ctx = {
    isAdmin: isPrivilegedUser(user),
    canBill: canViewBilling(),
    orgId: Number(user.organization_id ?? user.org_id ?? user.organizationId),
    featureOn: isFeatureEnabled,
  };
  const tabs = TAB_GROUPS[here.group].filter((tab) => tabShown(tab, ctx));
  if (tabs.length < 2) return null;
  // A group whose tabs share a filter bar carries its picks, so switching tabs keeps them.
  const current = new URLSearchParams(search);
  const carried = new URLSearchParams();
  (TAB_CARRY[here.group] || []).forEach((k) => { if (current.get(k)) carried.set(k, current.get(k)); });
  const linkTo = (to) => {
    if (!carried.toString()) return to;
    const [path, query = ''] = String(to).split('?');
    const merged = new URLSearchParams(query);
    carried.forEach((v, k) => merged.set(k, v));
    return `${path}?${merged.toString()}`;
  };

  return (
    <nav className="pillar-tabstrip" role="tablist" aria-label={t(`nav:pillarTabs.groups.${here.group}`)}>
      {tabs.map((tab) => {
        const active = tab.key === here.tab;
        return (
          <Link
            key={tab.key}
            to={linkTo(tab.to)}
            role="tab"
            aria-selected={active}
            className={`pillar-tab${active ? ' is-active' : ''}`}
          >
            <FeatherIcon name={tab.icon} size={14} />
            {t(tab.labelKey)}
          </Link>
        );
      })}
    </nav>
  );
}
